import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  VaultApi,
  VaultRequestRecord,
  VaultRequestEvent,
  EngineConfigResponse,
  EngineConfigUpdatePayload,
  AlertLogLevel,
} from './VaultApi';

export class VaultClient implements VaultApi {
  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  private async baseUrl(): Promise<string> {
    return this.options.discoveryApi.getBaseUrl('vault');
  }

  async getRequest(
    requestId: string,
  ): Promise<{ request: VaultRequestRecord; events: VaultRequestEvent[] }> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(
      `${base}/requests/${requestId}`,
      { method: 'GET' },
    );
    if (!resp.ok) throw new Error(`Failed to fetch request: ${resp.status}`);
    return resp.json();
  }

  async listUserRequests(requester: string): Promise<VaultRequestRecord[]> {
    const base = await this.baseUrl();
    const url = `${base}/requests?requester=${encodeURIComponent(requester)}`;
    const resp = await this.options.fetchApi.fetch(url, { method: 'GET' });
    if (!resp.ok) throw new Error(`Failed to list requests: ${resp.status}`);
    return resp.json();
  }

  async listRequestsByBsn(bsn: string): Promise<VaultRequestRecord[]> {
    const base = await this.baseUrl();
    const url = `${base}/requests?bsn=${encodeURIComponent(bsn)}`;
    const resp = await this.options.fetchApi.fetch(url, { method: 'GET' });
    if (!resp.ok)
      throw new Error(`Failed to list requests by bsn: ${resp.status}`);
    return resp.json();
  }

  async listAllRequests(adminEmail: string): Promise<VaultRequestRecord[]> {
    const base = await this.baseUrl();
    const url = `${base}/admin/requests?email=${encodeURIComponent(
      adminEmail,
    )}`;
    const resp = await this.options.fetchApi.fetch(url, { method: 'GET' });
    if (!resp.ok)
      throw new Error(`Failed to list all requests: ${resp.status}`);
    return resp.json();
  }

  async abandonRequest(
    requestId: string,
    adminEmail: string,
    note: string,
  ): Promise<void> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(
      `${base}/requests/${requestId}/abandon`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminEmail, note }),
      },
    );
    if (!resp.ok) {
      const body = (await resp.json().catch(() => ({}))) as any;
      throw new Error(body.error ?? `Abandon failed: ${resp.status}`);
    }
  }

  async cancelRequest(
    requestId: string,
    requester: string,
  ): Promise<{
    teardownStatus: 'ok' | 'skipped' | 'auth_failed' | 'partial';
    skippedPaths: string[];
  }> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(
      `${base}/requests/${requestId}?requester=${encodeURIComponent(requester)}`,
      { method: 'DELETE' },
    );
    if (!resp.ok) {
      const body = (await resp.json().catch(() => ({}))) as any;
      throw new Error(body.error ?? `Cancel failed: ${resp.status}`);
    }
    return resp.json();
  }

  async checkAdmin(email: string): Promise<{ isAdmin: boolean }> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(
      `${base}/admin/check?email=${encodeURIComponent(email)}`,
      { method: 'GET' },
    );
    if (!resp.ok) throw new Error(`Admin check failed: ${resp.status}`);
    return resp.json();
  }

  async getAlertLogLevel(email: string): Promise<{ logLevel: AlertLogLevel }> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(
      `${base}/admin/alert-settings?email=${encodeURIComponent(email)}`,
      { method: 'GET' },
    );
    if (!resp.ok)
      throw new Error(`Failed to fetch alert log level: ${resp.status}`);
    return resp.json();
  }

  async setAlertLogLevel(
    email: string,
    logLevel: AlertLogLevel,
  ): Promise<void> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(
      `${base}/admin/alert-settings`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, logLevel }),
      },
    );
    if (!resp.ok) {
      const body = (await resp.json().catch(() => ({}))) as any;
      throw new Error(
        body.error ?? `Failed to set alert log level: ${resp.status}`,
      );
    }
  }

  async retryCleanup(requestId: string, requester: string): Promise<void> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(
      `${base}/requests/${requestId}/retry-cleanup`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requester }),
      },
    );
    if (!resp.ok) {
      const body = (await resp.json().catch(() => ({}))) as any;
      throw new Error(body.error ?? `Retry cleanup failed: ${resp.status}`);
    }
  }

  async retryRequest(requestId: string, adminEmail: string): Promise<void> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(
      `${base}/requests/${requestId}/retry`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminEmail }),
      },
    );
    if (!resp.ok) {
      const body = (await resp.json().catch(() => ({}))) as any;
      throw new Error(body.error ?? `Retry failed: ${resp.status}`);
    }
  }

  async retryAll(
    adminEmail: string,
  ): Promise<{ queued: number; skipped: number }> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(`${base}/admin/retry-all`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: adminEmail }),
    });
    if (!resp.ok) {
      const body = (await resp.json().catch(() => ({}))) as any;
      throw new Error(body.error ?? `Retry all failed: ${resp.status}`);
    }
    return resp.json();
  }

  async checkOpaHealth(): Promise<{ reachable: boolean }> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(`${base}/opa-health`, {
      method: 'GET',
    });
    if (!resp.ok) return { reachable: false };
    return resp.json();
  }

  async getEngineConfig(requestId: string): Promise<EngineConfigResponse> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(
      `${base}/requests/${requestId}/engines`,
      { method: 'GET' },
    );
    if (!resp.ok) throw new Error(`Engine config fetch failed: ${resp.status}`);
    return resp.json();
  }

  async initiateDecommission(
    requestId: string,
    reason: string,
    justification: string,
    requester: string,
  ): Promise<void> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(
      `${base}/requests/${requestId}/decommission`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, justification, requester }),
      },
    );
    if (!resp.ok) {
      const body = (await resp.json().catch(() => ({}))) as any;
      throw new Error(
        body.error ?? `Decommission request failed: ${resp.status}`,
      );
    }
  }

  async editDatacenters(
    requestId: string,
    targetClusters: string[],
    requester: string,
  ): Promise<{ requestId: string }> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(
      `${base}/requests/${requestId}/edit-datacenters`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetClusters, requester }),
      },
    );
    const body = (await resp.json().catch(() => ({}))) as any;
    if (!resp.ok) {
      throw new Error(
        body.error ?? `Change Datacenters request failed: ${resp.status}`,
      );
    }
    return body;
  }

  async requestEngineUpdate(
    requestId: string,
    updates: EngineConfigUpdatePayload,
    requester: string,
  ): Promise<{ requestId: string }> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(
      `${base}/requests/${requestId}/engine-config`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...updates, requester }),
      },
    );
    const body = (await resp.json().catch(() => ({}))) as any;
    if (!resp.ok) {
      throw new Error(
        body.error ?? `Engine config edit request failed: ${resp.status}`,
      );
    }
    return body;
  }

  async addEnvironment(
    requestId: string,
    environment: string,
    requester: string,
  ): Promise<{ requestId: string }> {
    const base = await this.baseUrl();
    const resp = await this.options.fetchApi.fetch(
      `${base}/requests/${requestId}/add-environment`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ environment, requester }),
      },
    );
    const body = (await resp.json().catch(() => ({}))) as any;
    if (!resp.ok) {
      throw new Error(
        body.error ?? `Add Environment request failed: ${resp.status}`,
      );
    }
    return body;
  }
}
